<template>
    <section class="border border-white/20 rounded-md p-3 space-y-3 bg-white/10">
        <div class="flex items-start justify-between gap-3">
            <div>
                <p class="text-gray-300 font-semibold text-xs">GPIO</p>
                <p class="mt-1 text-[11px] text-gray-400">
                    Physical GPIO slots and logical firmware entities for {{ plate?.name || 'this plate' }}.
                </p>
            </div>
            <span class="shrink-0 rounded border px-2 py-0.5 text-[10px]"
                :class="validation.valid ? 'border-emerald-700/60 text-emerald-300' : 'border-red-700/60 text-red-300'">
                {{ validation.valid ? 'Valid' : `${validation.errors.length} issue(s)` }}
            </span>
        </div>

        <div class="flex flex-wrap gap-2">
            <button type="button" class="rounded bg-slate-700 px-3 py-1.5 text-xs text-white hover:bg-slate-600 disabled:opacity-50"
                :disabled="!canUseDevice || isBusy" @click="loadDevice">
                {{ isLoading ? 'Loading…' : 'Load from device' }}
            </button>
            <button type="button" class="rounded bg-ui-active px-3 py-1.5 text-xs text-white hover:opacity-90 disabled:opacity-50"
                :disabled="isBusy || !validation.valid" @click="saveLocally">
                Save locally
            </button>
            <button type="button" class="rounded bg-blue-600 px-3 py-1.5 text-xs text-white hover:bg-blue-500 disabled:opacity-50"
                :disabled="!canUseDevice || isBusy || !validation.valid" @click="deploy">
                {{ isDeploying ? 'Deploying…' : 'Deploy to device' }}
            </button>
        </div>

        <p v-if="!canUseDevice" class="rounded border border-slate-600 bg-slate-900/30 px-2 py-1.5 text-[11px] text-slate-400">
            Device load/deploy is available from the desktop build. Local plate configuration remains available offline.
        </p>
        <p v-if="status.message" class="rounded border px-2 py-1.5 text-[11px]"
            :class="status.kind === 'error' ? 'border-red-700/60 bg-red-950/20 text-red-300' : status.kind === 'success' ? 'border-emerald-700/60 bg-emerald-950/20 text-emerald-300' : 'border-slate-600 text-slate-300'">
            {{ status.message }}
        </p>

        <div class="space-y-2">
            <div class="flex items-center justify-between">
                <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-300">GPIO slots</h3>
                <button type="button" class="rounded bg-slate-700 px-2 py-1 text-[11px] text-white hover:bg-slate-600 disabled:opacity-50"
                    :disabled="firstFreeSlot < 0" @click="addPhysicalSlot">Add slot</button>
            </div>
            <div class="overflow-x-auto rounded border border-white/10">
                <table class="w-full min-w-[42rem] text-[11px]">
                    <thead class="bg-slate-900/40 text-gray-500 uppercase tracking-wide">
                        <tr>
                            <th class="px-2 py-1.5 text-left">Slot</th>
                            <th class="px-2 py-1.5 text-left">Pin</th>
                            <th class="px-2 py-1.5 text-left">Type</th>
                            <th class="px-2 py-1.5 text-left">Function</th>
                            <th class="px-2 py-1.5 text-left">Channel</th>
                            <th class="px-2 py-1.5 text-left">Settings</th>
                            <th class="px-2 py-1.5 text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-white/10">
                        <tr v-for="(slot, index) in draft.slots" :key="index" :class="slot.type === GPIO_TYPE.FREE ? 'text-gray-600' : 'text-gray-200'">
                            <td class="px-2 py-1.5 font-mono">{{ index }}</td>
                            <td class="px-2 py-1.5">
                                <input v-if="slot.type !== GPIO_TYPE.FREE" v-model.number="slot.pin" type="number" min="0" max="255"
                                    class="w-16 rounded border border-white/20 bg-white/10 px-1 py-1 text-white focus:outline-none focus:border-white/40"
                                    @input="markDirty" />
                                <span v-else>—</span>
                            </td>
                            <td class="px-2 py-1.5">
                                <span v-if="slot.type !== GPIO_TYPE.FREE" class="block font-medium">{{ gpioSlotTypeLabel(slot) }}</span>
                                <span v-if="slot.type !== GPIO_TYPE.FREE" class="block text-[10px] text-gray-500">{{ slotTypeLabel(slot.type) }}</span>
                                <span v-else>—</span>
                            </td>
                            <td class="px-2 py-1.5">
                                <select v-if="slot.type !== GPIO_TYPE.FREE" :value="slot.function_id" class="w-32 rounded border border-white/20 bg-slate-800 px-1 py-1 text-white focus:outline-none focus:border-white/40"
                                    @change="setSlotFunction(index, $event.target.value)">
                                    <option v-for="option in GPIO_SLOT_FUNCTIONS" :key="option.id" :value="option.id">{{ option.label }}</option>
                                </select>
                                <span v-else>—</span>
                            </td>
                            <td class="px-2 py-1.5">
                                <select v-if="slot.type !== GPIO_TYPE.FREE && slotFunctionChannels(slot.function_id).length > 1" :value="slot.channel" class="w-28 rounded border border-white/20 bg-slate-800 px-1 py-1 text-white focus:outline-none focus:border-white/40" @change="setSlotChannel(index, $event.target.value)">
                                    <option v-for="channel in slotFunctionChannels(slot.function_id)" :key="channel" :value="channel">{{ roleLabel(channel) }}</option>
                                </select>
                                <span v-else-if="slot.type !== GPIO_TYPE.FREE">{{ roleLabel(slot.channel) || '—' }}</span>
                                <span v-else>—</span>
                            </td>
                            <td class="px-2 py-1.5">
                                <div v-if="slot.type !== GPIO_TYPE.FREE" class="flex flex-wrap items-center gap-2">
                                    <label v-if="isInputSlot(slot)" class="flex items-center gap-1 text-[10px] text-gray-400">
                                        Pull
                                        <select v-model.number="slot.gpioFunction" class="w-28 rounded border border-white/20 bg-slate-800 px-1 py-1 text-white focus:outline-none focus:border-white/40" @change="markDirty">
                                            <option v-for="option in GPIO_PULL_OPTIONS.filter(item => item.value !== GPIO_FUNCTION.OUTPUT)" :key="option.value" :value="option.value">{{ option.label }}</option>
                                        </select>
                                    </label>
                                    <label v-if="!isAdcSlot(slot)" class="flex items-center gap-1 text-[10px] text-gray-400">
                                        <input v-model="slot.inverted" type="checkbox" class="rounded border-white/20 bg-white/10 text-ui-active" @change="markDirty" />
                                        Invert
                                    </label>
                                    <label class="flex items-center gap-1 text-[10px] text-gray-400">
                                        Group
                                        <input v-model.number="slot.group" type="number" min="0" max="255" class="w-14 rounded border border-white/20 bg-white/10 px-1 py-1 text-white focus:outline-none focus:border-white/40" @input="markDirty" />
                                    </label>
                                </div>
                                <span v-else>—</span>
                            </td>
                            <td class="px-2 py-1.5 text-right">
                                <button v-if="slot.type !== GPIO_TYPE.FREE" type="button" class="text-red-300 hover:text-red-200" @click="freePhysicalSlot(index)">Free</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div class="space-y-2">
            <div class="flex items-center justify-between">
                <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-300">GPIO entities</h3>
                <button type="button" class="rounded bg-slate-700 px-2 py-1 text-[11px] text-white hover:bg-slate-600 disabled:opacity-50"
                    :disabled="draft.entities.length >= GPIO_ENTITY_MAX" @click="addEntity">Add entity</button>
            </div>

            <div v-if="!draft.entities.length" class="rounded border border-dashed border-white/15 px-3 py-4 text-center text-xs text-gray-500">
                No logical GPIO entities configured.
            </div>

            <div v-for="(entity, entityIndex) in draft.entities" :key="entityIndex" class="rounded border border-white/15 bg-slate-900/20 p-2.5 space-y-2">
                <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <label class="text-[11px] text-gray-400">ID
                        <input v-model.trim="entity.id" type="text" maxlength="23" class="mt-1 w-full rounded border border-white/20 bg-white/10 px-2 py-1 text-white focus:outline-none focus:border-white/40" @input="markDirty" />
                    </label>
                    <label class="text-[11px] text-gray-400">Name
                        <input v-model="entity.name" type="text" maxlength="31" class="mt-1 w-full rounded border border-white/20 bg-white/10 px-2 py-1 text-white focus:outline-none focus:border-white/40" @input="markDirty" />
                    </label>
                </div>
                <div class="space-y-2">
                    <p class="text-[11px] text-gray-400">GPIO slots</p>
                    <div v-if="configuredSlots.length" class="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        <label v-for="channel in entityEditorChannels(entity)" :key="channel.role" class="text-[11px] text-gray-400">
                            {{ channel.label }} channel
                            <select :value="selectedSlotForRole(entity, channel.role)" class="mt-1 w-full rounded border border-white/20 bg-slate-800 px-2 py-1 text-white focus:outline-none focus:border-white/40" @change="setEntityChannel(entity, channel.role, $event.target.value)">
                                <option value="">Select slot…</option>
                                <option v-for="candidate in slotCandidatesForEntity(entity, channel.role)" :key="candidate.index" :value="candidate.index">
                                    Slot {{ candidate.index }} · GPIO {{ candidate.slot.pin ?? 'unset' }} · {{ roleLabel(candidate.slot.channel) || slotTypeLabel(candidate.slot.type) }}
                                </option>
                            </select>
                        </label>
                    </div>
                    <p v-else class="text-[11px] text-gray-500">Configure a GPIO slot first.</p>
                </div>
                <div v-if="entity.function_id === 'adc_sensor'" class="grid grid-cols-1 gap-2 sm:grid-cols-2 rounded border border-white/10 bg-slate-900/20 p-2">
                    <p class="sm:col-span-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">ADC configuration</p>
                    <label class="text-[11px] text-gray-400">Raw minimum
                        <input v-model.number="entity.adc.raw_min" type="number" min="0" max="65535" step="1" class="mt-1 w-full rounded border border-white/20 bg-white/10 px-2 py-1 text-white focus:outline-none focus:border-white/40" @input="markDirty" />
                    </label>
                    <label class="text-[11px] text-gray-400">Raw maximum
                        <input v-model.number="entity.adc.raw_max" type="number" min="0" max="65535" step="1" class="mt-1 w-full rounded border border-white/20 bg-white/10 px-2 py-1 text-white focus:outline-none focus:border-white/40" @input="markDirty" />
                    </label>
                    <label class="text-[11px] text-gray-400">Output minimum
                        <input v-model.number="entity.adc.output_min" type="number" step="any" class="mt-1 w-full rounded border border-white/20 bg-white/10 px-2 py-1 text-white focus:outline-none focus:border-white/40" @input="markDirty" />
                    </label>
                    <label class="text-[11px] text-gray-400">Output maximum
                        <input v-model.number="entity.adc.output_max" type="number" step="any" class="mt-1 w-full rounded border border-white/20 bg-white/10 px-2 py-1 text-white focus:outline-none focus:border-white/40" @input="markDirty" />
                    </label>
                    <label class="text-[11px] text-gray-400">Unit
                        <input v-model="entity.adc.unit" type="text" maxlength="15" class="mt-1 w-full rounded border border-white/20 bg-white/10 px-2 py-1 text-white focus:outline-none focus:border-white/40" @input="markDirty" />
                    </label>
                    <label class="text-[11px] text-gray-400">Sample interval (ms)
                        <input v-model.number="entity.adc.interval_ms" type="number" min="10" max="60000" step="1" class="mt-1 w-full rounded border border-white/20 bg-white/10 px-2 py-1 text-white focus:outline-none focus:border-white/40" @input="markDirty" />
                    </label>
                    <label class="text-[11px] text-gray-400">Change threshold
                        <input v-model.number="entity.adc.change_threshold" type="number" min="0" step="any" class="mt-1 w-full rounded border border-white/20 bg-white/10 px-2 py-1 text-white focus:outline-none focus:border-white/40" @input="markDirty" />
                    </label>
                </div>
                <div class="flex items-center justify-between gap-2">
                    <div class="text-[11px] text-gray-400">
                        Properties: <span class="text-gray-200">{{ functionDefinition(entity)?.properties.join(', ') || '—' }}</span>
                        <span v-if="functionDefinition(entity)?.events?.length" class="ml-2">Events: <span class="text-gray-200">{{ functionDefinition(entity).events.join(', ') }}</span></span>
                    </div>
                    <button type="button" class="text-[11px] text-red-300 hover:text-red-200" @click="removeEntity(entityIndex)">Remove entity</button>
                </div>
            </div>
        </div>

        <div v-if="validation.errors.length || validation.warnings.length" class="space-y-1 rounded border border-white/10 bg-slate-900/20 px-2 py-2 text-[11px]">
            <p v-for="error in validation.errors" :key="`error-${error}`" class="text-red-300">✕ {{ error }}</p>
            <p v-for="warning in validation.warnings" :key="`warning-${warning}`" class="text-amber-300">⚠ {{ warning }}</p>
        </div>
    </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import {
    GPIO_ENTITY_MAX,
    GPIO_FUNCTION,
    GPIO_PULL_OPTIONS,
    GPIO_SLOT_FUNCTIONS,
    GPIO_TYPE,
    createEmptyGpioConfig,
    emptyGpioSlot,
    getGpioHardwareProfile,
    getGpioEntityFunction,
    gpioSlotFunctionChannels,
    gpioSlotTypeLabel,
    inferGpioEntityFunction,
    gpioTypeForSlotFunction,
    normalizeGpioConfig,
    serializeGpioConfig,
    slotTypeLabel,
    validateGpioPlateConfig,
} from '../../../gpio/gpioPlateConfig.js'
import { deployDeviceGpioConfig, loadDeviceGpioConfig } from '../../../services/gpioConfigService.js'

const props = defineProps({
    plate: { type: Object, required: true },
    appSettings: { type: Object, required: true },
})
const emit = defineEmits(['save', 'deployed'])

const draft = ref(createEmptyGpioConfig())
const dirty = ref(false)
const isLoading = ref(false)
const isDeploying = ref(false)
const status = ref({ kind: 'idle', message: '' })

const canUseDevice = computed(() => Boolean(props.appSettings?.tauri && props.plate?.host))
const isBusy = computed(() => isLoading.value || isDeploying.value)
const validation = computed(() => validateGpioPlateConfig(draft.value, {
    hardwareProfile: getGpioHardwareProfile(props.plate, props.appSettings),
}))
const firstFreeSlot = computed(() => draft.value.slots.findIndex(slot => slot.type === GPIO_TYPE.FREE))
const configuredSlots = computed(() => draft.value.slots
    .map((slot, index) => ({ slot, index }))
    .filter(item => item.slot.type !== GPIO_TYPE.FREE))

function resetFromPlate() {
    draft.value = normalizeGpioConfig(props.plate?.gpio_config)
    dirty.value = false
    status.value = { kind: 'idle', message: '' }
}

watch(() => props.plate?.id, resetFromPlate, { immediate: true })
watch(() => props.plate?.gpio_config, value => {
    if (!dirty.value) draft.value = normalizeGpioConfig(value)
}, { deep: true })

function markDirty() {
    dirty.value = true
    status.value = { kind: 'idle', message: '' }
}

function derivedEntityFunctionId(entity) {
    const selectedSlots = (entity.channels ?? [])
        .map(channel => draft.value.slots[channel.slot])
        .filter(Boolean)
    const selectedFunctions = new Set(selectedSlots.map(slot => slot.function_id).filter(Boolean))
    if (selectedFunctions.has('rgbww_light') || selectedSlots.some(slot => [GPIO_TYPE.LED_CW, GPIO_TYPE.LED_WW].includes(slot.type))) return 'rgbww_light'
    if (selectedFunctions.has('rgb_light')) return 'rgb_light'
    return inferGpioEntityFunction(entity, draft.value.slots)
}

function functionDefinition(entity) {
    const functionId = derivedEntityFunctionId(entity)
    return getGpioEntityFunction(functionId) ?? null
}

function roleLabel(role) {
    return ({ state: 'State', value: 'Value', brightness: 'Brightness', red: 'Red', green: 'Green', blue: 'Blue', cool_white: 'Cool white', warm_white: 'Warm white', white: 'White' })[role] ?? role
}

function slotFunctionChannels(functionId) {
    return gpioSlotFunctionChannels(functionId)
}

function isInputSlot(slot) {
    return slot.type >= 0x80
}

function isAdcSlot(slot) {
    return slot.type === GPIO_TYPE.HASP_ADC
}

function roleForSlot(slot) {
    return slot.channel || ({
        [GPIO_TYPE.SWITCH]: 'state',
        [GPIO_TYPE.BUTTON]: 'state',
        [GPIO_TYPE.POWER_RELAY]: 'state',
        [GPIO_TYPE.LIGHT_RELAY]: 'state',
        [GPIO_TYPE.PWM]: 'brightness',
        [GPIO_TYPE.LED_R]: 'red',
        [GPIO_TYPE.LED_G]: 'green',
        [GPIO_TYPE.LED_B]: 'blue',
        [GPIO_TYPE.LED_CW]: 'cool_white',
        [GPIO_TYPE.LED_WW]: 'warm_white',
        [GPIO_TYPE.LED_W]: 'white',
        [GPIO_TYPE.HASP_ADC]: 'value',
    })[slot.type] || null
}

function entityHasSlot(entity, index) {
    return entity.channels.some(channel => channel.slot === index)
}

function selectedSlotForRole(entity, role) {
    return entity.channels.find(channel => channel.role === role)?.slot ?? ''
}

function entityEditorChannels(entity) {
    const definition = functionDefinition(entity)
    return definition?.channels.map(channel => ({ role: channel.role, label: roleLabel(channel.role) })) ?? [{ role: 'slot', label: 'GPIO' }]
}

function slotMatchesEntityChannel(slot, role, definition) {
    if (!definition || role === 'slot') return true
    const expected = definition.channels.find(channel => channel.role === role)
    if (!expected) return false
    if (slot.function_id && slot.function_id !== definition.id) return false
    if (expected.type === GPIO_TYPE.SWITCH && ![GPIO_TYPE.SWITCH, GPIO_TYPE.BUTTON].includes(slot.type)) return false
    return slot.type === expected.type
}

function slotCandidatesForEntity(entity, role) {
    const definition = functionDefinition(entity)
    const currentSlot = selectedSlotForRole(entity, role)
    const assignedByOther = new Set(draft.value.entities
        .filter(candidate => candidate !== entity)
        .flatMap(candidate => candidate.channels.map(channel => channel.slot)))
    entity.channels
        .filter(channel => channel.role !== role)
        .forEach(channel => assignedByOther.add(channel.slot))
    return configuredSlots.value.filter(candidate => {
        if (candidate.index !== currentSlot && assignedByOther.has(candidate.index)) return false
        return candidate.index === currentSlot || slotMatchesEntityChannel(candidate.slot, role, definition)
    })
}

function syncEntityFromSlots(entity) {
    const selected = [...new Set(entity.channels.map(channel => channel.slot).filter(slot => Number.isInteger(slot)))]
        .sort((left, right) => left - right)
    entity.channels = selected.map(slotIndex => ({ role: roleForSlot(draft.value.slots[slotIndex]), slot: slotIndex }))
    const functionId = derivedEntityFunctionId(entity)
    const definition = getGpioEntityFunction(functionId)
    entity.function_id = functionId
    entity.domain = definition?.domain ?? ''
    entity.capabilities = definition ? [...definition.properties] : []
    if (definition?.adc && !entity.adc) {
        entity.adc = { raw_min: 0, raw_max: 4095, output_min: 0, output_max: 1, interval_ms: 100, change_threshold: 0, unit: '' }
    }
}

function rederiveEntities() {
    draft.value.entities.forEach(syncEntityFromSlots)
}

function setEntityChannel(entity, role, value) {
    const slotIndex = value === '' ? null : Number(value)
    if (slotIndex !== null && !Number.isInteger(slotIndex)) return
    const otherEntity = slotIndex === null ? null : draft.value.entities.find(candidate => candidate !== entity && entityHasSlot(candidate, slotIndex))
    if (otherEntity) {
        status.value = { kind: 'error', message: `GPIO-slot ${slotIndex} is al aan een andere entity toegewezen.` }
        return
    }
    if (slotIndex !== null && entity.channels.some(channel => channel.role !== role && channel.slot === slotIndex)) {
        status.value = { kind: 'error', message: `GPIO-slot ${slotIndex} kan niet aan meerdere kanalen van dezelfde entity worden toegewezen.` }
        return
    }
    if (role === 'slot') entity.channels = []
    else entity.channels = entity.channels.filter(channel => channel.role !== role)
    if (slotIndex !== null) entity.channels.push({ role: role === 'slot' ? roleForSlot(draft.value.slots[slotIndex]) : role, slot: slotIndex })
    syncEntityFromSlots(entity)
    markDirty()
}

function addPhysicalSlot() {
    const index = firstFreeSlot.value
    if (index < 0) return
    draft.value.slots[index] = { ...emptyGpioSlot(), index, type: GPIO_TYPE.POWER_RELAY, function_id: 'digital_output', channel: 'state', gpioFunction: GPIO_FUNCTION.OUTPUT }
    markDirty()
}

function setSlotFunction(index, functionId) {
    const current = draft.value.slots[index]
    const definition = GPIO_SLOT_FUNCTIONS.find(option => option.id === functionId)
    if (!definition) return
    const channel = definition.channels.includes(current.channel) ? current.channel : definition.channels[0]
    draft.value.slots[index] = {
        ...current,
        function_id: functionId,
        channel,
        type: gpioTypeForSlotFunction(functionId, channel),
        gpioFunction: functionId === 'digital_input' || functionId === 'adc_sensor' ? GPIO_FUNCTION.INTERNAL_PULLUP : GPIO_FUNCTION.OUTPUT,
    }
    rederiveEntities()
    markDirty()
}

function setSlotChannel(index, channel) {
    const slot = draft.value.slots[index]
    slot.channel = channel
    slot.type = gpioTypeForSlotFunction(slot.function_id, channel)
    rederiveEntities()
    markDirty()
}

function freePhysicalSlot(index) {
    if (draft.value.entities.some(entity => entityHasSlot(entity, index))) {
        status.value = { kind: 'error', message: `Slot ${index} is nog gekoppeld aan een entity.` }
        return
    }
    draft.value.slots[index] = { ...emptyGpioSlot(), index }
    markDirty()
}

function addEntity() {
    const id = nextEntityId()
    const entity = { id, name: id, function_id: null, domain: '', capabilities: [], channels: [] }
    draft.value.entities.push(entity)
    markDirty()
}

function nextEntityId() {
    let index = 1
    while (draft.value.entities.some(entity => entity.id === `gpio${index}`)) index++
    return `gpio${index}`
}

function removeEntity(index) {
    draft.value.entities.splice(index, 1)
    markDirty()
}

function saveLocally() {
    if (!validation.value.valid) return
    const payload = serializeGpioConfig(draft.value)
    emit('save', payload)
    dirty.value = false
    status.value = { kind: 'success', message: 'GPIO-configuratie lokaal opgeslagen.' }
}

async function loadDevice() {
    isLoading.value = true
    status.value = { kind: 'idle', message: '' }
    try {
        const result = await loadDeviceGpioConfig(props.plate)
        draft.value = result.config
        dirty.value = false
        // Store the device's configuration with the plate right away, so the
        // Designer can use its entities without a separate save.
        emit('save', serializeGpioConfig(result.config))
        status.value = { kind: 'success', message: 'GPIO-configuratie van de plate geladen en lokaal bij de plate opgeslagen.' }
    } catch (error) {
        status.value = { kind: 'error', message: error.message }
    } finally {
        isLoading.value = false
    }
}

async function deploy() {
    if (!validation.value.valid) return
    isDeploying.value = true
    status.value = { kind: 'idle', message: '' }
    try {
        const result = await deployDeviceGpioConfig(props.plate, draft.value)
        draft.value = result.verified
        dirty.value = false
        // The confirmed device configuration is the plate's configuration now.
        emit('save', result.payload)
        emit('deployed', result.verified)
        status.value = { kind: 'success', message: 'GPIO-configuratie is door de plate bevestigd.' }
    } catch (error) {
        status.value = { kind: 'error', message: error.message }
    } finally {
        isDeploying.value = false
    }
}
</script>
